import React from 'react';
import { cn } from '@/lib/utils';

export interface DiretoriaGaugeProps {
  label: string;
  value: number;
  meta: number | null;
  /** Optional subtitle under the value */
  hint?: string;
  className?: string;
}

/** Radial gauge: value vs meta (0–100%+ capped visually at 120%). */
export const DiretoriaGauge: React.FC<DiretoriaGaugeProps> = ({
  label,
  value,
  meta,
  hint,
  className,
}) => {
  const pct = meta != null && meta > 0 ? (value / meta) * 100 : null;
  const visual = pct == null ? 0 : Math.min(120, Math.max(0, pct));
  const angle = (visual / 100) * 180; // semicircle
  const tone =
    pct == null
      ? 'text-muted-foreground'
      : pct >= 100
        ? 'text-emerald-700'
        : pct >= 70
          ? 'text-amber-700'
          : 'text-rose-700';

  const r = 54;
  const cx = 70;
  const cy = 70;
  const startX = cx - r;
  const startY = cy;
  const rad = ((180 - angle) * Math.PI) / 180;
  const endX = cx + r * Math.cos(rad);
  const endY = cy - r * Math.sin(rad);
  const large = angle > 180 ? 1 : 0;

  return (
    <div className={cn('flex flex-col items-center text-center', className)}>
      <svg viewBox="0 0 140 90" className="h-24 w-36" aria-hidden>
        <path
          d={`M ${startX} ${startY} A ${r} ${r} 0 0 1 ${cx + r} ${cy}`}
          fill="none"
          stroke="currentColor"
          strokeWidth="10"
          className="text-muted/40"
          strokeLinecap="round"
        />
        {pct != null && angle > 0 ? (
          <path
            d={`M ${startX} ${startY} A ${r} ${r} 0 ${large} 1 ${endX} ${endY}`}
            fill="none"
            stroke="currentColor"
            strokeWidth="10"
            className={tone}
            strokeLinecap="round"
          />
        ) : null}
      </svg>
      <p className="mt-[-0.5rem] text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
        {label}
      </p>
      <p className={cn('font-heading text-2xl font-bold tabular-nums', tone)}>
        {value.toLocaleString('pt-BR')}
        {meta != null ? (
          <span className="text-sm font-normal text-muted-foreground">
            {' '}
            / {meta.toLocaleString('pt-BR')}
          </span>
        ) : null}
      </p>
      {pct != null ? (
        <p className="text-xs text-muted-foreground">{pct.toFixed(0)}% da meta</p>
      ) : (
        <p className="text-xs text-muted-foreground">Defina a meta</p>
      )}
      {hint ? <p className="mt-1 text-[11px] text-muted-foreground">{hint}</p> : null}
    </div>
  );
};
